import React, { useState, useEffect, useRef } from 'react';
import toast, { LoaderIcon } from 'react-hot-toast';
import { FaCheckCircle } from 'react-icons/fa';
import { FiAlertCircle } from 'react-icons/fi';
import { LuX } from 'react-icons/lu';
import authService from '../../../services/api/auth.service';

interface OTPVerificationModalProps {
    emailModalOpen: boolean;
    setEmailModalOpen: (isOpen: boolean) => void;
    email: string;
}

const VeriFyEmailOTP: React.FC<OTPVerificationModalProps> = ({
    emailModalOpen,
    setEmailModalOpen,
    email,
}) => {
    const [otp, setOtp] = useState<string[]>(Array(6).fill(''));
    const [activeInput, setActiveInput] = useState<number>(0);
    const [error, setError] = useState<string>('');
    const [isVerifying, setIsVerifying] = useState<boolean>(false);
    const [isSuccess, setIsSuccess] = useState<boolean>(false);
    const [countdown, setCountdown] = useState<number>(0);
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    useEffect(() => {
        if (emailModalOpen) {
            if (inputRefs.current[0]) {
                inputRefs.current[0].focus();
            }
            // Start the countdown when modal opens
            setCountdown(30);
        }
    }, [emailModalOpen]);

    useEffect(() => {
        if (!emailModalOpen) {
            setOtp(Array(6).fill(''));
            setError('');
            setIsSuccess(false);
            setIsVerifying(false);
            setCountdown(0);
        }
    }, [emailModalOpen]);

    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (countdown > 0) {
            timer = setInterval(() => {
                setCountdown(prev => prev - 1);
            }, 1000);
        }
        return () => clearInterval(timer);
    }, [countdown]);

    const handleInputChange = (index: number, value: string) => {
        if (value.length > 1) return;

        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);
        setError('');

        if (value && index < 5) {
            setActiveInput(index + 1);
            inputRefs.current[index + 1]?.focus();
        }

        if (newOtp.every(digit => digit !== '')) {
            handleVerify(newOtp.join(''));
        }
    };

    const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            setActiveInput(index - 1);
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handleVerify = async (otpString: string) => {
        setIsVerifying(true);
        setError('');

        try {
            const response = await authService.verifyOTP({ email, email_otp: otpString });
            toast.success(response.message);
            setIsSuccess(true);
            setTimeout(() => {
                setEmailModalOpen(false);
            }, 1500);
        } catch (err) {
            setError((err as any).response.data.message);
        } finally {
            setIsVerifying(false);
        }
    };

    const handleResendOTP = async () => {
        try {
            await authService.sendEmailOTP({email});
            toast.success('OTP sent successfully');
            setCountdown(30);
        } catch (err) {
            toast.error('Failed to send OTP');
        }
    };

    if (!emailModalOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div
                className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
                onClick={() => setEmailModalOpen(false)}
            />

            <div className="relative w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-8 shadow-xl transition-all">
                <div className="absolute right-4 top-4">
                    <button
                        onClick={() => setEmailModalOpen(false)}
                        className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
                    >
                        <LuX size={20} />
                    </button>
                </div>

                <div className="text-center">
                    {isSuccess ? (
                        <div className="flex flex-col items-center space-y-4">
                            <FaCheckCircle className="h-12 w-12 text-green-500" />
                            <h3 className="text-xl font-semibold text-gray-900">Verification Successful!</h3>
                        </div>
                    ) : (
                        <>
                            <h3 className="text-xl font-semibold text-gray-900">Email Verification</h3>
                            <p className="mt-2 text-sm text-gray-600">
                                We've sent a verification code to<br />
                                <span className="font-medium text-gray-900">{email}</span>
                            </p>

                            <div className="mt-8 flex justify-center space-x-3">
                                {otp.map((digit, idx) => (
                                    <input
                                        key={idx}
                                        ref={(el: HTMLInputElement | null) => { inputRefs.current[idx] = el; }}
                                        type="text"
                                        maxLength={1}
                                        value={digit}
                                        onChange={e => handleInputChange(idx, e.target.value)}
                                        onKeyDown={e => handleKeyDown(idx, e)}
                                        className={`h-12 w-12 rounded-lg border text-center text-lg font-semibold
                                            ${activeInput === idx
                                                ? 'border-primary-800 bg-primary-50'
                                                : 'border-gray-200 bg-gray-50'
                                            } 
                                            focus:border-primary-800 focus:bg-primary-50 focus:outline-none
                                            transition-all duration-200`}
                                    />
                                ))}
                            </div>

                            {error && (
                                <div className="mt-4 flex items-center justify-center text-red-500 text-sm">
                                    <FiAlertCircle className="mr-1 h-4 w-4" />
                                    {error}
                                </div>
                            )}

                            {isVerifying && (
                                <div className="mt-4 flex items-center justify-center text-primary-800">
                                    <LoaderIcon className="mr-2 h-5 w-5 animate-spin" />
                                    Verifying...
                                </div>
                            )}

                            <p className="mt-6 text-sm text-gray-600">
                                Didn't receive the code?{' '}
                                {countdown > 0 ? (
                                    <span className="text-gray-400">
                                        Resend in {countdown}s
                                    </span>
                                ) : (
                                    <button
                                        onClick={handleResendOTP}
                                        className="font-semibold text-primary-600 hover:text-primary-800"
                                    >
                                        Resend
                                    </button>
                                )}
                            </p>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default VeriFyEmailOTP;